import { datadogRum } from "@datadog/browser-rum";
import { isConfigured, isInitialized } from "./index";

// Allowed primitive attr values. Keep attrs flat to match Datadog's
// addAction(name, context) shape and query-friendly RUM facets.
type ActionAttrPrimitive = string | number | boolean | null;

// Map of every action name this app emits to the exact attrs shape allowed.
// Keep keys here in sync with src/integrations/datadog/README.md if you add new
// flows. PII is restricted to the opaque id and low-cardinality role already
// exposed by setUser; action attrs exclude those fields.
export interface ActionMap {
  "auth.login.completed": {
    method: "password" | "sms_otp";
  };
  "auth.login.failed": {
    method: "password" | "password_reset" | "sms_otp";
    error_code: string;
  };
  "auth.logout": Record<string, never>;
  "auth.logout.remote_revocation_failed": Record<string, never>;
  "cta.fab.quick_action.clicked": {
    action_id: string;
    destination: string;
  };
  "estimate.created": {
    line_count: number;
    is_quick: boolean;
    is_sample: boolean;
    merged_into_existing: boolean;
  };
  "inventory.product.created": {
    quantity: number | null;
    has_category: boolean;
  };
  "invoice.created": {
    line_count: number;
    merged_into_existing: boolean;
  };
  "portal_payment.approved": {
    status: string;
    target_kind: "customer" | "vendor";
  };
  "purchase_order.created": {
    product_count: number;
    has_initial_payment: boolean;
    merged_into_existing: boolean;
  };
}

export type ActionName = keyof ActionMap;

/** Sends a typed, flat, non-PII custom action to Datadog RUM when configured. */
export function trackAction<N extends ActionName>(
  name: N,
  ...args: ActionMap[N] extends Record<string, never> ? [] : [attrs: ActionMap[N]]
): void {
  if (!(isConfigured() && isInitialized())) {
    return;
  }
  const attrs = (args[0] ?? {}) as Record<string, ActionAttrPrimitive>;
  datadogRum.addAction(name, attrs);
}
